import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2, Users2, Plus, Copy, Link as LinkIcon, TrendingUp, Download,
  CheckCircle2, PowerOff, Euro, Eye,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const eur = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const COMMISSION_GRID = [
  { label: 'Pro / Expert mensuel', pct: '15 % one-shot' },
  { label: 'Pack 3 mois (Pro/Expert)', pct: '20 % one-shot' },
  { label: 'Annuel (Pro/Expert)', pct: '25 % one-shot' },
  { label: 'Packs analyses (25/40/70 €)', pct: '15 % one-shot' },
];

export default function AdminAffiliates() {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', code: '', siret: '', payout_email: '', notes: '' });
  const [statsAffiliate, setStatsAffiliate] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [payoutsPreview, setPayoutsPreview] = useState(null);
  const [processingCycle, setProcessingCycle] = useState(false);

  const loadAffiliates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/affiliates`);
      setAffiliates(data.affiliates || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPayouts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/affiliates/payouts/preview`);
      setPayoutsPreview(data);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => { loadAffiliates(); loadPayouts(); }, [loadAffiliates, loadPayouts]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (!payload[k]) delete payload[k]; });
      const { data } = await axios.post(`${API}/admin/affiliates`, payload);
      toast.success(`Affilié créé — Code: ${data.code}`);
      setShowCreate(false);
      setForm({ name: '', email: '', code: '', siret: '', payout_email: '', notes: '' });
      loadAffiliates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création');
    } finally {
      setCreating(false);
    }
  };

  const handleDeactivate = async (aff) => {
    if (!window.confirm(`Désactiver l'affilié ${aff.name} (${aff.code}) ? Les commissions en cours restent valables.`)) return;
    try {
      await axios.delete(`${API}/admin/affiliates/${aff.id}`);
      toast.success('Affilié désactivé');
      loadAffiliates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleReactivate = async (aff) => {
    try {
      await axios.patch(`${API}/admin/affiliates/${aff.id}`, { active: true });
      toast.success('Affilié réactivé');
      loadAffiliates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const copyLink = (code) => {
    const base = window.location.origin;
    const url = `${base}/r/${code}`;
    navigator.clipboard.writeText(url).then(() => {
      toast.success(`Lien copié — ${url}`);
    }).catch(() => toast.error('Copie impossible'));
  };

  const openStats = async (aff) => {
    setStatsAffiliate(aff);
    setStatsData(null);
    try {
      const { data } = await axios.get(`${API}/admin/affiliates/${aff.id}/stats`);
      setStatsData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur stats');
    }
  };

  const approveEligible = async () => {
    setProcessingCycle(true);
    try {
      const { data } = await axios.post(`${API}/admin/affiliates/conversions/approve-eligible`);
      toast.success(`${data.approved} conversion(s) approuvée(s) (rétractation J+15 écoulée)`);
      loadPayouts();
      loadAffiliates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setProcessingCycle(false);
    }
  };

  const markCyclePaid = async () => {
    if (!window.confirm('Marquer comme PAYÉES toutes les commissions approuvées ≥ 50 € ? À faire APRÈS avoir effectué les virements bancaires.')) return;
    setProcessingCycle(true);
    try {
      const { data } = await axios.post(`${API}/admin/affiliates/mark-cycle-paid`);
      toast.success(`${data.marked_paid} commissions marquées payées pour ${data.affiliates_paid} affilié(s)`);
      loadPayouts();
      loadAffiliates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setProcessingCycle(false);
    }
  };

  const downloadCsv = () => {
    // Redirect the browser directly so the download dialog appears
    const link = document.createElement('a');
    link.href = `${API}/admin/affiliates/payouts/export.csv`;
    link.download = 'affiliate-payouts.csv';
    link.click();
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>;

  const totalPending = affiliates.reduce((s, a) => s + (a.pending_commission_eur || 0), 0);
  const totalPaid = affiliates.reduce((s, a) => s + (a.paid_commission_eur || 0), 0);

  return (
    <div className="space-y-4" data-testid="admin-affiliates-panel">
      {/* Header */}
      <Card className="p-4 flex flex-wrap gap-3 items-center justify-between">
        <div className="flex items-center gap-3">
          <Users2 className="w-6 h-6 text-emerald-400" />
          <div>
            <div className="text-lg font-bold">Programme d&apos;affiliation</div>
            <div className="text-xs text-muted-foreground">
              {affiliates.length} affilié(s) · {eur(totalPending)} en attente · {eur(totalPaid)} déjà payés
            </div>
          </div>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)} className="bg-emerald-500 hover:bg-emerald-600 text-black" data-testid="add-affiliate-btn">
          <Plus className="w-4 h-4 mr-1" /> Ajouter un affilié
        </Button>
      </Card>

      {/* Grid rémunération info */}
      <Card className="p-4 border-amber-500/20 bg-amber-500/5">
        <div className="flex items-start gap-3">
          <TrendingUp className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-sm w-full">
            <div className="font-semibold text-amber-300 mb-2">Grille de commissions active</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
              {COMMISSION_GRID.map((g) => (
                <div key={g.label} className="p-2 rounded bg-white/5 border border-white/10">
                  <div className="text-muted-foreground">{g.label}</div>
                  <div className="font-mono text-emerald-400 mt-0.5">{g.pct}</div>
                </div>
              ))}
            </div>
            <div className="text-xs text-muted-foreground mt-3">
              Bonus filleul : -10 % à l&apos;inscription · 50 analyses (Pro mensuel) · +3 assets Scan Auto (Expert mensuel) · Payout J+15 · min 50 €
            </div>
          </div>
        </div>
      </Card>

      {/* Table affiliés */}
      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 border-b border-white/10 text-muted-foreground">
            <tr>
              <th className="text-left p-3">Code</th>
              <th className="text-left p-3">Nom</th>
              <th className="text-left p-3">Email</th>
              <th className="text-right p-3">Clics</th>
              <th className="text-right p-3">Conversions</th>
              <th className="text-right p-3">En attente</th>
              <th className="text-right p-3">Payé</th>
              <th className="text-center p-3">Statut</th>
              <th className="text-center p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {affiliates.map(a => (
              <tr key={a.id} className="border-b border-white/5" data-testid={`affiliate-row-${a.code}`}>
                <td className="p-3 font-mono font-bold text-emerald-400">{a.code}</td>
                <td className="p-3">{a.name}</td>
                <td className="p-3 text-xs text-muted-foreground">{a.email}</td>
                <td className="p-3 text-right font-mono">{a.clicks_total}</td>
                <td className="p-3 text-right font-mono">{a.conversions_total}</td>
                <td className="p-3 text-right font-mono text-amber-400">{eur(a.pending_commission_eur)}</td>
                <td className="p-3 text-right font-mono text-emerald-400">{eur(a.paid_commission_eur)}</td>
                <td className="p-3 text-center">
                  {a.active ? (
                    <span className="text-xs text-emerald-400">● Actif</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">○ Inactif</span>
                  )}
                </td>
                <td className="p-3">
                  <div className="flex justify-center gap-1">
                    <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => copyLink(a.code)} title="Copier le lien" data-testid={`copy-link-btn-${a.code}`}>
                      <LinkIcon className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => openStats(a)} title="Stats détaillées" data-testid={`view-stats-btn-${a.code}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    {a.active ? (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-red-400" onClick={() => handleDeactivate(a)} title="Désactiver" data-testid={`deactivate-btn-${a.code}`}>
                        <PowerOff className="w-3.5 h-3.5" />
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-emerald-400" onClick={() => handleReactivate(a)} title="Réactiver">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {affiliates.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">Aucun affilié pour l&apos;instant. Ajoutez-en un pour démarrer.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* Cycle payout management */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Euro className="w-5 h-5 text-amber-400" />
          <div className="font-semibold">Cycle de paiement</div>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          <Button size="sm" variant="outline" onClick={approveEligible} disabled={processingCycle} data-testid="approve-eligible-btn">
            {processingCycle ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            Approuver les rétractations écoulées (J+15)
          </Button>
          <Button size="sm" variant="outline" onClick={downloadCsv} data-testid="export-payouts-csv-btn">
            <Download className="w-4 h-4 mr-1" /> Export CSV payouts
          </Button>
          <Button size="sm" onClick={markCyclePaid} disabled={processingCycle} className="bg-emerald-500 hover:bg-emerald-600 text-black" data-testid="mark-cycle-paid-btn">
            <Euro className="w-4 h-4 mr-1" /> Marquer cycle payé
          </Button>
        </div>

        {payoutsPreview && payoutsPreview.payouts?.length > 0 ? (
          <table className="w-full text-xs">
            <thead className="text-muted-foreground border-b border-white/10">
              <tr>
                <th className="text-left p-2">Code</th>
                <th className="text-left p-2">Nom</th>
                <th className="text-left p-2">Payout email</th>
                <th className="text-right p-2">Conversions</th>
                <th className="text-right p-2">Montant</th>
                <th className="text-center p-2">Éligible</th>
              </tr>
            </thead>
            <tbody>
              {payoutsPreview.payouts.map((p) => (
                <tr key={p.affiliate_id} className="border-b border-white/5">
                  <td className="p-2 font-mono text-emerald-400">{p.affiliate_code}</td>
                  <td className="p-2">{p.name}</td>
                  <td className="p-2 text-muted-foreground">{p.payout_email}</td>
                  <td className="p-2 text-right font-mono">{p.conversions_count}</td>
                  <td className="p-2 text-right font-mono">{eur(p.total_eur)}</td>
                  <td className="p-2 text-center">
                    {p.eligible_this_cycle
                      ? <span className="text-emerald-400">✓</span>
                      : <span className="text-muted-foreground" title={p.reason_if_held}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-xs text-muted-foreground p-4 text-center">Aucune commission approuvée pour ce cycle.</div>
        )}
      </Card>

      {/* Create modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg" data-testid="create-affiliate-modal">
          <DialogHeader>
            <DialogTitle>Ajouter un affilié</DialogTitle>
            <DialogDescription>
              Le code sera généré automatiquement si laissé vide. Un lien de suivi <code>/r/CODE</code> sera disponible.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <Label htmlFor="aff-name">Nom / Pseudo *</Label>
              <Input id="aff-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Jean YouTube" data-testid="affiliate-name-input" />
            </div>
            <div>
              <Label htmlFor="aff-email">Email *</Label>
              <Input id="aff-email" type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="jean@youtube.com" data-testid="affiliate-email-input" />
            </div>
            <div>
              <Label htmlFor="aff-code">Code personnalisé (optionnel)</Label>
              <Input id="aff-code" value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} placeholder="JEAN20 (laisser vide = auto)" maxLength={20} data-testid="affiliate-code-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="aff-siret">SIRET (optionnel)</Label>
                <Input id="aff-siret" value={form.siret} onChange={(e) => setForm({...form, siret: e.target.value})} placeholder="Requis pour payout" />
              </div>
              <div>
                <Label htmlFor="aff-payout">Email payout (optionnel)</Label>
                <Input id="aff-payout" type="email" value={form.payout_email} onChange={(e) => setForm({...form, payout_email: e.target.value})} placeholder="Si différent de email" />
              </div>
            </div>
            <div>
              <Label htmlFor="aff-notes">Notes internes</Label>
              <Input id="aff-notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Contexte, chaîne YouTube, etc." />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
              <Button type="submit" disabled={creating} className="bg-emerald-500 hover:bg-emerald-600 text-black" data-testid="submit-affiliate-btn">
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                Créer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stats modal */}
      <Dialog open={!!statsAffiliate} onOpenChange={(o) => { if (!o) { setStatsAffiliate(null); setStatsData(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="affiliate-stats-modal">
          <DialogHeader>
            <DialogTitle>
              Statistiques — {statsAffiliate?.name} <span className="font-mono text-emerald-400 ml-2">[{statsAffiliate?.code}]</span>
            </DialogTitle>
            <DialogDescription>
              Détail des clics, conversions et commissions pour cet affilié
            </DialogDescription>
          </DialogHeader>
          {!statsData ? (
            <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 rounded border border-white/10 bg-white/5">
                  <div className="text-xs text-muted-foreground">Clics</div>
                  <div className="text-xl font-bold">{statsData.clicks_total}</div>
                </div>
                <div className="p-3 rounded border border-white/10 bg-white/5">
                  <div className="text-xs text-muted-foreground">Conversions</div>
                  <div className="text-xl font-bold">{statsData.conversions_total}</div>
                </div>
                <div className="p-3 rounded border border-white/10 bg-white/5">
                  <div className="text-xs text-muted-foreground">Filleuls uniques</div>
                  <div className="text-xl font-bold">{statsData.unique_referrals}</div>
                </div>
                <div className="p-3 rounded border border-white/10 bg-white/5">
                  <div className="text-xs text-muted-foreground">Taux conv.</div>
                  <div className="text-xl font-bold">{statsData.conversion_rate_pct}%</div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 rounded border border-amber-500/20 bg-amber-500/5">
                  <div className="text-xs text-muted-foreground">En attente</div>
                  <div className="text-lg font-bold text-amber-400">{eur(statsData.totals_eur.pending)}</div>
                </div>
                <div className="p-3 rounded border border-blue-500/20 bg-blue-500/5">
                  <div className="text-xs text-muted-foreground">Approuvées</div>
                  <div className="text-lg font-bold text-blue-400">{eur(statsData.totals_eur.approved)}</div>
                </div>
                <div className="p-3 rounded border border-emerald-500/20 bg-emerald-500/5">
                  <div className="text-xs text-muted-foreground">Payées</div>
                  <div className="text-lg font-bold text-emerald-400">{eur(statsData.totals_eur.paid)}</div>
                </div>
                <div className="p-3 rounded border border-red-500/20 bg-red-500/5">
                  <div className="text-xs text-muted-foreground">Annulées</div>
                  <div className="text-lg font-bold text-red-400">{eur(statsData.totals_eur.reversed)}</div>
                </div>
              </div>

              <div>
                <div className="text-sm font-semibold mb-2">Dernières conversions</div>
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground border-b border-white/10">
                    <tr>
                      <th className="text-left p-2">Date</th>
                      <th className="text-left p-2">Filleul</th>
                      <th className="text-left p-2">Formule</th>
                      <th className="text-left p-2">Type</th>
                      <th className="text-right p-2">Payé</th>
                      <th className="text-right p-2">Commission</th>
                      <th className="text-center p-2">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statsData.recent_conversions.map((c) => (
                      <tr key={c.id || `${c.stripe_event_id}-${c.user_id}-${c.created_at}`} className="border-b border-white/5">
                        <td className="p-2 text-muted-foreground">{c.created_at?.substring(0, 10)}</td>
                        <td className="p-2">{c.user_email}</td>
                        <td className="p-2">{c.tier_label}</td>
                        <td className="p-2 text-xs uppercase">{c.event_kind}</td>
                        <td className="p-2 text-right font-mono">{eur(c.gross_amount_paid)}</td>
                        <td className="p-2 text-right font-mono text-emerald-400">{eur(c.commission_amount)}</td>
                        <td className="p-2 text-center text-xs uppercase">{c.status}</td>
                      </tr>
                    ))}
                    {statsData.recent_conversions.length === 0 && (
                      <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">Aucune conversion pour l&apos;instant</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
